import { CampaignStatus } from '../marketing-campaign-builder/builder-context';

import { dayBreakpoints, emptyAccumulatorByChannel, monthBreakpoints } from './consts';
import { CHANNEL_TYPES, REVENUE_TYPES } from './types';

import type { AttributionStats, ChannelPerformanceStats, FilteredChannelDataMap } from './types';
import type { FunnelItemsProps } from 'src/app/components/lib/funnel-item/funnel';
import type { CampaignDurationInfo } from 'src/app/queries/marketing/estimate-campaign-send-duration';
import type { AutomatedCampaignPerformanceStats } from 'src/app/queries/marketing/get-automated-campaign-performance';
import type { BlastCampaignPerformanceStats } from 'src/app/queries/marketing/get-campaign-performance';
import type { SavedMarketingCampaign } from 'src/app/queries/marketing/get-campaigns';
import type { Segment } from 'src/app/queries/segment/get-segments';

export const selectedAudienceCount = (selectedSegments?: number[], segments?: Segment[]) => {
  if (!selectedSegments || !segments) {
    return 0;
  }
  const selectedIdLookup = new Set(selectedSegments);
  const isTargetedByEmail = (segment) => selectedIdLookup.has(segment.SegmentId);
  return segments.reduce(
    (accumulator, segment) => accumulator + (isTargetedByEmail(segment) ? segment.AudienceSize : 0),
    0
  );
};

export const defaultAttributionStats: AttributionStats = { Carts: 0, Customers: 0, Orders: 0, TotalRevenue: 0 };

export const ensureAttributionStats = (stats: AttributionStats | undefined): AttributionStats =>
  stats ?? defaultAttributionStats;

export const sumAttributionStats = (acc: AttributionStats, curr: AttributionStats): AttributionStats => ({
  Carts: acc.Carts + curr.Carts,
  Customers: acc.Customers + curr.Customers,
  Orders: acc.Orders + curr.Orders,
  TotalRevenue: acc.TotalRevenue + curr.TotalRevenue,
});

export const sumMultipleAttributionStats = (stats: AttributionStats[]): AttributionStats =>
  stats?.reduce(sumAttributionStats, defaultAttributionStats);

export const accumulateChannelStats = (
  acc: ChannelPerformanceStats,
  curr: AutomatedCampaignPerformanceStats | BlastCampaignPerformanceStats,
  channel: CHANNEL_TYPES
): ChannelPerformanceStats => {
  let directAttribution = ensureAttributionStats(acc.DirectAttribution);
  let openersInStore = ensureAttributionStats(acc.OpenersInStoreAttribution);
  let openersOnline = ensureAttributionStats(acc.OpenersOnlineAttribution);

  let recipients = acc.Recipients ?? 0;
  let delivered = acc.Delivered ?? 0;
  let opened = acc.Opened ?? 0;
  let clickedLink = acc.ClickedLink ?? 0;
  let unsubscribes = acc.Unsubscribes ?? 0;
  let failedDeliveries = acc.FailedDeliveries ?? 0;

  if (channel === CHANNEL_TYPES.EMAIL || channel === CHANNEL_TYPES.ALL) {
    directAttribution = sumAttributionStats(directAttribution, ensureAttributionStats(curr.EmailDirectAttribution));
    openersInStore = sumAttributionStats(openersInStore, ensureAttributionStats(curr.EmailOpenersInStoreAttribution));
    openersOnline = sumAttributionStats(openersOnline, ensureAttributionStats(curr.EmailOpenersOnlineAttribution));
    recipients += curr.EmailRecipients;
    delivered += curr.EmailDelivered;
    opened += curr.EmailOpened;
    clickedLink += curr.EmailClickedLink;
    unsubscribes += curr.EmailUnsubscribes;
    failedDeliveries += curr.EmailFailedDeliveries;
  }
  if (channel === CHANNEL_TYPES.SMS || channel === CHANNEL_TYPES.ALL) {
    directAttribution = sumAttributionStats(directAttribution, ensureAttributionStats(curr.SMSDirectAttribution));
    openersInStore = sumAttributionStats(openersInStore, ensureAttributionStats(curr.SMSOpenersInStoreAttribution));
    openersOnline = sumAttributionStats(openersOnline, ensureAttributionStats(curr.SMSOpenersOnlineAttribution));
    recipients += curr.SMSRecipients;
    delivered += curr.SMSDelivered;
    opened += curr.SMSVerified;
    clickedLink += curr.SMSClicked;
    unsubscribes += curr.SMSUnsubscribes;
    failedDeliveries += curr.SMSFailedDeliveries;
  }
  if (channel === CHANNEL_TYPES.PUSH || channel === CHANNEL_TYPES.ALL) {
    // no direct attribution for push
    openersInStore = sumAttributionStats(openersInStore, ensureAttributionStats(curr.PushOpenersInStoreAttribution));
    openersOnline = sumAttributionStats(openersOnline, ensureAttributionStats(curr.PushOpenersOnlineAttribution));
    recipients += curr.PushRecipients;
    delivered += curr.PushRecipients; // for the purposes of delivery rate and the "all" funnel, just assume all push messages were delivered
    opened += curr.PushOpened;
  }

  return {
    ...acc,
    Recipients: recipients,
    Delivered: delivered,
    Opened: opened,
    ClickedLink: clickedLink,
    Unsubscribes: unsubscribes,
    FailedDeliveries: failedDeliveries,
    DirectAttribution: directAttribution,
    AllRevenueAttribution: sumMultipleAttributionStats([directAttribution, openersInStore, openersOnline]),
    OpenersInStoreAttribution: openersInStore,
    OpenersOnlineAttribution: openersOnline,
  };
};

export function filteredBlastCampaignDataByChannel(
  campaignData: BlastCampaignPerformanceStats[],
  channel: CHANNEL_TYPES
): FilteredChannelDataMap {
  const breakpoints = dayBreakpoints;

  if (!campaignData?.length) {
    return breakpoints.reduce<FilteredChannelDataMap>((result, breakpoint) => {
      result[breakpoint] = emptyAccumulatorByChannel;
      return result;
    }, {});
  }

  return breakpoints.reduce<FilteredChannelDataMap>((result, breakpoint) => {
    const filteredData = campaignData.filter((item) => (item?.CampaignDay ?? 0) < breakpoint);

    result[breakpoint] = filteredData.reduce(
      (acc, curr) => accumulateChannelStats(acc, curr, channel),
      emptyAccumulatorByChannel
    );

    return result;
  }, {});
}

export function filteredAutomatedCampaignDataByChannel(
  campaignData: AutomatedCampaignPerformanceStats[],
  channel: CHANNEL_TYPES
): FilteredChannelDataMap {
  const breakpoints = monthBreakpoints;

  if (!campaignData?.length) {
    return breakpoints.reduce<FilteredChannelDataMap>((result, breakpoint) => {
      result[breakpoint] = emptyAccumulatorByChannel;
      return result;
    }, {});
  }

  return breakpoints.reduce<FilteredChannelDataMap>((result, breakpoint) => {
    const filteredData = campaignData.filter((item) => (item?.MonthsBack ?? 0) <= breakpoint);

    result[breakpoint] = filteredData.reduce(
      (acc, curr) => accumulateChannelStats(acc, curr, channel),
      emptyAccumulatorByChannel
    );

    return result;
  }, {});
}

export function getFunnelItems(
  data: ChannelPerformanceStats,
  channelDropdownOption: CHANNEL_TYPES,
  revenueDropdownOption: REVENUE_TYPES
): FunnelItemsProps[] {
  let channelFunnelItems: FunnelItemsProps[] = [];
  switch (channelDropdownOption) {
    case CHANNEL_TYPES.ALL:
      channelFunnelItems = [
        {
          statName: 'Delivered',
          statValue: data.Delivered ?? 0,
          tooltip: 'The number of delivered messages across all channels',
        },
        {
          statName: 'Opened',
          statValue: data.Opened ?? 0,
          tooltip: 'The number of opened messages across all channels',
        },
      ];
      break;
    case CHANNEL_TYPES.EMAIL:
      channelFunnelItems = [
        {
          statName: 'Delivered',
          statValue: data.Delivered ?? 0,
          tooltip: 'The number of customers who received an email',
        },
        {
          statName: 'Opened',
          statValue: data.Opened ?? 0,
          tooltip: 'The number of customers who opened an email',
        },
        {
          statName: 'Clicked link',
          statValue: data.ClickedLink ?? 0,
          tooltip: 'The number of customers who clicked a link in an email',
        },
      ];
      break;
    case CHANNEL_TYPES.SMS:
      channelFunnelItems = [
        {
          statName: 'Delivered',
          statValue: data.Delivered ?? 0,
          tooltip: 'The number of customers who received an SMS/MMS message',
        },
        {
          statName: 'Clicked link',
          statValue: data.ClickedLink ?? 0,
          tooltip: 'The number of customers who clicked the link in the SMS/MMS message',
        },
        {
          statName: 'Verified birthday',
          statValue: data.Opened ?? 0,
          tooltip: 'The number of customers who passed the verification step and viewed the campaign content',
        },
      ];
      break;
    case CHANNEL_TYPES.PUSH:
      channelFunnelItems = [
        {
          statName: 'Recipients',
          statValue: data.Recipients ?? 0,
          tooltip: 'The number of push notifications sent',
        },
        {
          statName: 'Opened',
          statValue: data.Opened ?? 0,
          tooltip: 'The number of customers who opened the push notification',
        },
      ];
  }

  if (revenueDropdownOption === REVENUE_TYPES.ALL_REVENUE || revenueDropdownOption === REVENUE_TYPES.DIRECT_REVENUE) {
    channelFunnelItems.push({
      statName: 'Carts created',
      statValue: data[revenueDropdownOption]?.Carts ?? 0,
      tooltip: 'The number of customers who added a product to their cart',
    });
    channelFunnelItems.push({
      statName: 'Placed order',
      statValue: data[revenueDropdownOption]?.Orders ?? 0,
      tooltip: 'The number of customers who placed an order',
    });
  } else if (revenueDropdownOption === REVENUE_TYPES.INDIRECT_ONLINE_REVENUS) {
    channelFunnelItems.push({
      statName: 'Placed order',
      statValue: data[revenueDropdownOption]?.Orders ?? 0,
      tooltip: 'The number of customers who placed an order',
    });
  } else if (revenueDropdownOption === REVENUE_TYPES.INDIRECT_IN_STORE_REVENUS) {
    channelFunnelItems.push({
      statName: 'Purchased in store',
      statValue: data[revenueDropdownOption]?.Orders ?? 0,
      tooltip: 'The number of customers who purchased in store',
    });
  }

  return channelFunnelItems;
}

export function hasQueuedOrSendingCampaignsInLsp(
  campaignQueue: CampaignDurationInfo[],
  currentLspId: number,
  currentCampaignId: number
): boolean {
  if (!campaignQueue || campaignQueue.length === 0) {
    return false;
  }

  return campaignQueue.some(
    (campaign) =>
      campaign.LspId === currentLspId &&
      campaign.MarketingCampaignId !== currentCampaignId &&
      (campaign.Status === CampaignStatus.SENDING || campaign.Status === CampaignStatus.QUEUED)
  );
}

/**
 * Formats a number with k/m suffixes for large numbers
 * Examples: 100 -> "100", 1500 -> "1.5k", 25000 -> "25k", 1500000 -> "1.5m"
 */
export function formatLargeNumber(num: number): string {
  if (num < 1000) {
    return num.toString();
  }

  if (num < 1000000) {
    const thousands = num / 1000;
    if (thousands % 1 === 0) {
      return `${thousands}k`;
    }
    const rounded = Math.round(thousands * 100) / 100;
    return `${rounded}k`;
  }

  if (num < 1000000000) {
    const millions = num / 1000000;
    if (millions % 1 === 0) {
      return `${millions}m`;
    }
    const rounded = Math.round(millions * 100) / 100;
    return `${rounded}m`;
  }

  const billions = num / 1000000000;
  if (billions % 1 === 0) {
    return `${billions}b`;
  }
  const rounded = Math.round(billions * 100) / 100;
  return `${rounded}b`;
}

/**
 * Formats the sending progress display string (e.g., "50/100" or "25k/100k")
 */
export function formatSendingProgress(campaign: SavedMarketingCampaign): string {
  const totalSent =
    (campaign.EmailConfig?.TotalSent ?? 0) +
    (campaign.SMSConfig?.TotalSent ?? 0) +
    (campaign.PushConfig?.TotalSent ?? 0);

  const totalToSend =
    (campaign.EmailConfig?.TotalToSend ?? 0) +
    (campaign.SMSConfig?.TotalToSend ?? 0) +
    (campaign.PushConfig?.TotalToSend ?? 0);

  if (totalToSend === 0) {
    return '--';
  }

  return `${formatLargeNumber(totalSent)}/${formatLargeNumber(totalToSend)}`;
}
